import React, { useState, useEffect, useCallback } from 'react';
import { TokenResponse, POE_AUTH_CONFIG } from '../../types';
import { initiatePoEAuthentication } from '../../utils/poeAuth';

interface PoeAuthWindowProps {
  onAuthSuccess: (token: string, username: string | undefined, expiresAt: number) => void;
  onError: (message: string) => void;
  isAuthenticated: boolean;
  username: string | null;
  tokenExpiresAt: number | null;
  onLogout: () => void;
}

export const PoeAuthWindow: React.FC<PoeAuthWindowProps> = ({
  onAuthSuccess,
  onError,
  isAuthenticated,
  username,
  tokenExpiresAt,
  onLogout
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Handle OAuth code exchange via server proxy
  const exchangeCodeForToken = useCallback(async (code: string, state: string) => {
    const storedState = localStorage.getItem('oauth_state');
    const codeVerifier = localStorage.getItem('code_verifier');
    
    // Verify state matches to prevent CSRF
    if (state !== storedState) {
      onError('Security verification failed. Please try again.');
      return;
    }
    
    if (!codeVerifier) {
      onError('Security verification code not found. Please try again.');
      return;
    }
    
    // Clear stored state after verification
    localStorage.removeItem('oauth_state');
    
    try {
      setIsLoading(true);

      // Exchange code for token via our server proxy
      const tokenResponse = await fetch(POE_AUTH_CONFIG.tokenProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code,
          code_verifier: codeVerifier,
          redirect_uri: POE_AUTH_CONFIG.redirectUri
        })
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        throw new Error(errorData.error || 'Failed to obtain access token');
      }

      const tokenData: TokenResponse = await tokenResponse.json();
      
      // Calculate expiration
      const expiresAt = Date.now() + (tokenData.expires_in * 1000);
      
      // Pass token info to parent
      onAuthSuccess(
        tokenData.access_token, 
        tokenData.username, 
        expiresAt
      );
    } catch (err) {
      console.error('Authentication error:', err);
      onError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  }, [onAuthSuccess, onError]);

  // Check for authorization code in URL on component mount
  useEffect(() => {
    // Check for authorization code in query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get('code');
    const returnedState = urlParams.get('state');
    const errorParam = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    // Clear URL parameters to prevent issues with refreshing
    if (authCode || errorParam) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Handle OAuth errors
    if (errorParam) {
      onError(`Authentication error: ${errorParam}${errorDescription ? ` - ${errorDescription}` : ''}`);
      return;
    }

    if (authCode && returnedState) {
      exchangeCodeForToken(authCode, returnedState);
    }
  }, [exchangeCodeForToken, onError]);

  // Login component for unauthenticated users
  if (!isAuthenticated) {
    return (
      <div className="text-center">
        <div className="bg-blue-800 text-blue-100 p-3 rounded text-sm mb-4">
          <p>This feature lets you securely download information about your Idols from in-game stashes and find those matching current strategy.</p>
          <p>If you encounter any issues, please use the Report Bug button in lower-left corner to let me know!</p>
        </div>
        <h2 className="text-xl font-semibold mb-4">Connect your Path of Exile Account</h2>
        <p className="mb-6 text-gray-400">
          Connect your PoE account to access your stash tabs. Your data is only used in this browser session.
        </p>
        <button 
          onClick={initiatePoEAuthentication}
          className="bg-blue-600 hover:bg-blue-500 px-6 py-3 rounded-lg text-white font-medium transition duration-200 w-full max-w-md mx-auto"
          disabled={isLoading}
        >
          {isLoading ? 'Connecting...' : 'Connect Path of Exile Account'}
        </button>

        <div className="mt-6 text-sm text-gray-500">
          <p>Note: This uses OAuth 2.0 with PKCE to securely connect to your PoE account.</p>
          <p>No passwords are ever shared with this application.</p>
        </div>
      </div>
    );
  }

  // Profile component for authenticated users
  return (
    <div className="flex justify-between items-center mb-6">
      <div>
        <h2 className="text-xl font-semibold">Your PoE Account</h2>
        {username && (
          <p className="text-gray-400">Connected as: {username}</p>
        )}
        {tokenExpiresAt && (
          <p className="text-xs text-gray-500">
            Session expires: {new Date(tokenExpiresAt).toLocaleString()}
          </p>
        )}
      </div>
      <button 
        onClick={onLogout}
        className="bg-red-600 hover:bg-red-500 px-4 py-2 rounded text-white"
      >
        Disconnect
      </button>
    </div>
  );
};

export default PoeAuthWindow;
